import React from 'react';
import dataService from '../services/dataService';
import WikipediaCitation from './WikipediaCitation';

const StateDetails = ({ selectedState, allData = {} }) => {
    // Voter registration websites by state - verified and updated
    const voterRegistrationSites = {
        'AL': 'https://www.alabamainteractive.org/sos/voter_registration/',
        'AK': 'https://voterregistration.alaska.gov/',
        'AZ': 'https://azsos.gov/elections/voting-election/register-vote-or-update-your-current-voter-information',
        'AR': 'https://www.sos.arkansas.gov/elections/voter-information',
        'CA': 'https://registertovote.ca.gov/',
        'CO': 'https://www.sos.state.co.us/pubs/elections/vote/VoterHome.html',
        'CT': 'https://voterregistration.ct.gov/',
        'DE': 'https://elections.delaware.gov/voter/votereg.shtml',
        'FL': 'https://registertovoteflorida.gov/',
        'GA': 'https://registertovote.sos.ga.gov/',
        'HI': 'https://elections.hawaii.gov/register-to-vote/',
        'ID': 'https://elections.idaho.gov/elections/voter-registration/',
        'IL': 'https://ova.elections.il.gov/',
        'IN': 'https://indianavoters.in.gov/',
        'IA': 'https://sos.iowa.gov/elections/voterinformation/voterregistration.html',
        'KS': 'https://www.kdor.ks.gov/apps/voterreg/home/index',
        'KY': 'https://elect.ky.gov/registertovote/Pages/default.aspx',
        'LA': 'https://voterportal.sos.la.gov/VoterRegistration',
        'ME': 'https://www.maine.gov/sos/cec/elec/voter-info/voterguide.html',
        'MD': 'https://voterservices.elections.maryland.gov/OnlineVoterRegistration/InstructionsStep1',
        'MA': 'https://www.sec.state.ma.us/ovr/',
        'MI': 'https://mvic.sos.state.mi.us/RegisterVoter',
        'MN': 'https://www.sos.state.mn.us/elections-voting/register-to-vote/',
        'MS': 'https://www.sos.ms.gov/elections-voting/voter-registration-information',
        'MO': 'https://www.sos.mo.gov/elections/goVoteMissouri/register',
        'MT': 'https://sosmt.gov/elections/vote/',
        'NE': 'https://www.nebraska.gov/apps-sos-voter-registration/',
        'NV': 'https://www.nvsos.gov/sosvoterservices/Registration/step1.aspx',
        'NH': 'https://sos.nh.gov/elections/voters/register-to-vote/',
        'NJ': 'https://voter.svrs.nj.gov/register',
        'NM': 'https://portal.sos.state.nm.us/OVR/WebPages/InstructionsStep1.aspx',
        'NY': 'https://voterreg.dmv.ny.gov/MotorVoter/',
        'NC': 'https://www.ncsbe.gov/registering/how-register',
        'ND': 'https://vip.sos.nd.gov/WhereToVoteID.aspx',
        'OH': 'https://olvr.ohiosos.gov/',
        'OK': 'https://www.ok.gov/elections/Voter_Registration/',
        'OR': 'https://sos.oregon.gov/voting/Pages/registration.aspx',
        'PA': 'https://www.pavoterservices.pa.gov/Pages/VoterRegistrationApplication.aspx',
        'RI': 'https://vote.sos.ri.gov/Home/RegistertoVote',
        'SC': 'https://info.scvotes.sc.gov/eng/ovr/start.aspx',
        'SD': 'https://vip.sdsos.gov/VIPLogin.aspx',
        'TN': 'https://ovr.govote.tn.gov/',
        'TX': 'https://www.votetexas.gov/register-to-vote/',
        'UT': 'https://secure.utah.gov/voterreg/index.html',
        'VT': 'https://olvr.vermont.gov/',
        'VA': 'https://www.elections.virginia.gov/registration/how-to-register/',
        'WA': 'https://voter.votewa.gov/WhereToVote.aspx',
        'WV': 'https://ovr.sos.wv.gov/Register/Landing',
        'WI': 'https://myvote.wi.gov/en-us/RegisterToVote',
        'WY': 'https://sos.wyo.gov/Elections/State/RegisteringToVote.aspx'
    };

    if (!selectedState) {
        return (
            <div style={{
                padding: '20px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                margin: '20px 0'
            }}>
                <h3>State Details</h3>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    Click on a state to see detailed information
                </p>
            </div>
        );
    }

    const stateName = dataService.getStateName(selectedState);
    const dataTypesByCategory = dataService.getDataTypesByCategory();

    const formatValue = (dataType, value) => {
        if (value === null || value === undefined) return 'N/A';
        
        const source = dataService.dataSources[dataType];
        if (!source || !source.unit) return value;

        switch (source.unit) {
            case 'dollars':
                return `$${value.toLocaleString()}`;
            case 'dollars per hour':
                return `$${value.toFixed(2)}/hour`;
            case 'dollars per month':
                return `$${value.toLocaleString()}/month`;
            case 'percentage':
                return `${value}%`;
            case 'people':
                return value.toLocaleString();
            case 'years':
                return `${value} years`;
            case 'births per 1000 people':
                return `${value} per 1,000 people`;
            case 'deaths per 1000 births':
                return `${value} per 1,000 births`;
            case 'incidents per 100,000 people':
                return `${value} per 100,000 people`;
            case 'per 100,000 people':
                return `${value} per 100,000 people`;
            case 'people per square mile':
                return `${value} people/mi²`;
            case 'students per teacher':
                return `${value} students per teacher`;
            case 'degrees Fahrenheit':
                return `${value}°F`;
            case 'Mbps':
                return `${value} Mbps`;
            case 'metric tons per person':
                return `${value} tons CO₂/person`;
            case 'violations per 1000 people':
                return `${value} per 1,000 people`;
            case 'percentage of total energy':
                return `${value}% renewable`;
            case 'percentage of bridges':
                return `${value}% deficient`;
            case 'percentage of eligible voters':
                return `${value}% turnout`;
            case 'AQI (lower is better)':
                return `AQI ${value}`;
            case 'index (0-100)':
                return `${value}/100`;
            case 'index (higher is more affordable)':
                return `${value} (affordability index)`;
            case 'score (0-100, higher is better)':
                return `${value}/100`;
            case 'rank (1-50, lower is better)':
                return `Ranked #${value}`;
            case 'rank (1-50, lower is better/less corrupt)':
                return `Ranked #${value} (less corrupt is better)`;
            case 'grade (A+ to F, A+ is strongest)':
                return `Grade: ${value}`;
            default:
                return value;
        }
    };

    return (
        <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            margin: '20px 0',
            border: '2px solid #4CAF50',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '2px solid #4CAF50',
                paddingBottom: '10px',
                marginBottom: '20px'
            }}>
                <h3 style={{ 
                    color: '#4CAF50',
                    margin: 0
                }}>
                    {stateName} ({selectedState})
                </h3>
                
                {voterRegistrationSites[selectedState] && (
                    <a 
                        href={voterRegistrationSites[selectedState]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#45a049'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#4CAF50'}
                    >
                        🗳️ Register to Vote in {stateName}
                    </a>
                )}
            </div>
            
            {Object.entries(dataTypesByCategory).map(([category, dataTypes]) => {
                const categoryData = dataTypes.filter(({ key }) => allData && allData[key]);
                
                if (categoryData.length === 0) return null;
                
                return (
                    <div key={category} style={{ marginBottom: '25px' }}>
                        <h4 style={{
                            color: '#333',
                            marginBottom: '10px',
                            fontSize: '16px',
                            borderLeft: '4px solid #4CAF50',
                            paddingLeft: '10px'
                        }}>
                            {category}
                        </h4>
                        
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '10px',
                            marginLeft: '15px'
                        }}>
                            {categoryData.map(({ key, description }) => {
                                const value = allData[key] ? allData[key][selectedState] : null;
                                const formattedValue = formatValue(key, value);
                                
                                return (
                                    <div
                                        key={key}
                                        style={{
                                            padding: '10px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '4px',
                                            border: '1px solid #e9ecef',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <span style={{ 
                                            fontWeight: '500',
                                            color: '#495057'
                                        }}>
                                            {description}:
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                fontWeight: 'bold',
                                                color: value !== null && value !== undefined ? '#28a745' : '#6c757d',
                                                marginLeft: '10px'
                                            }}>
                                                {formattedValue}
                                            </span>
                                            <WikipediaCitation 
                                                metricKey={key}
                                                stateCode={selectedState}
                                                compact={true}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
            
            {Object.keys(allData).length === 0 && (
                <div style={{
                    padding: '20px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '4px',
                    color: '#856404',
                    textAlign: 'center'
                }}>
                    No data available. Select a metric above to load data.
                </div>
            )}
        </div>
    );
};

export default StateDetails;